import React, { useState, FC } from 'react'
import { useParams } from 'react-router-dom'
import { Trans } from 'react-i18next'
import * as jose from 'jose'
import t from '../../../../translations/main'
import { CORP_SEND_FILE_URL, DELIVERABLE_SEND_FILE_URL } from 'src/views/Pages/Onboarding/flowConfig'
import storage from 'src/components/Core/Utils/storage'
import globalConsts from '../../Onboarding/globalConsts'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import { useApplicationSelected, useProspectsApplications } from 'src/components/Core/Context/OnboardingContext'
import { filePayloadFactory } from 'src/components/Core/Utils/global'

import InputFiles from 'src/components/Atoms/InputFiles/InputFiles'
import Flex from 'src/components/Layout/Flex/Flex'
import Font from 'src/components/Core/Typography/Font/Font'
import { downloadPreview, flowKeyMap } from '../InputFilesGroup/InputFilesGroup'

import { InputFilesPerTypeFilesWrapper } from './InputFilesPerType.styles'
import { OnChangeType } from 'src/components/Atoms/InputFiles/types'
import { FilePerTypeProp, InputFilePerTypeProps } from './types'
import { ApiData } from 'src/views/Pages/Reports/Statements/types'

const token: string = storage?.get(storage?.keys?.main?.token)
//@ts-ignore:next-line
const userRole: string = token && jose?.decodeJwt(token)?.extension_UserStatus

const isEmployee = userRole === 'Employee'

const InputFilesPerType: FC<InputFilePerTypeProps> = props => {
  const { value, groupLabel, formType } = props
  const [filesFromServer, setFilesFromServer] = useState(value)

  const { flowKey } = useParams()

  const prospectsApplications = useProspectsApplications()
  const applicationSelected = useApplicationSelected()
  const { sendRequest, isLoading } = useFetch()

  const applicationId = storage.get(storage?.keys?.onboarding?.applicationId)

  const isDocumentation = prospectsApplications[`${applicationSelected}`].permission === 'documentation'

  const deleteFile = (documentID: string, callback:  () => void) => {
    sendRequest(
      {
        scope: 'onboarding',
        url: import.meta.env.VITE_MOCK_DATA !== 'true'
          ? `${getBaseApiHost()}/CorpAOF/RemoveDocument?applicationId=${applicationId}&DocumentID=${documentID}`
          : `${window.location.origin}/mock/deleteFile.json`,
        method: 'DELETE',
      },
      () => {
        const indexValue = filesFromServer.findIndex(doc => (doc.ppDocument.documentID.toString() === documentID || doc.ubDocument.documentID.toString() === documentID))
        if(indexValue === -1) {
          callback()
          return
        }
        const docTypePP = filesFromServer[`${indexValue}`].ppDocument.documentID.toString() === documentID
        const updatedValue = [...filesFromServer]
        updatedValue[`${indexValue}`][docTypePP ? 'ppDocument' : 'ubDocument'] = {
          documentID: 0,
          documentType: null,
          documentName: null,
          documentFileExtension: null,
          documentFileType: null,
          documentSection: null
        }
        setFilesFromServer(updatedValue)
        callback()
      }
    )
  }

  const onChange = async (file: OnChangeType, cb: (data: ApiData) => void, entry?: FilePerTypeProp, fileType?: string) => {

    sendRequest(
      {
        scope: 'onboarding',
        url: formType === 'Deliverable' ? DELIVERABLE_SEND_FILE_URL : CORP_SEND_FILE_URL,
        method: 'POST',
        body: await filePayloadFactory(file.data, props.step, fileType, applicationId, `${entry.type}/${entry.id}`),
      },
      (data => {
        // when the files are mandatory again this should be uncommented and checked if is updating the state properly
        // customOnChange()
        cb(data)
        return null
      })
    )
  }

  return (
    <Flex flexDirection='column' alignItems='flex-start'>
      {/* @ts-ignore */}
      <Font margin='0 0 16px 0'><Trans i18nKey={groupLabel} /></Font>
      {
        filesFromServer &&
        Array.isArray(filesFromServer) &&
        filesFromServer.length > 0
          ? filesFromServer.map((item, index) => {
            return (
              <InputFilesPerTypeFilesWrapper
                flexDirection='column'
                alignItems='flex-start'
                key={item.id}
                isDocumentation={isDocumentation || isEmployee}
              >
                <Font margin='8px 0 8px 4%'>Type: {
                  item.type === 'ubo'
                    ? item.type?.toUpperCase()
                    : item.type?.toLowerCase()?.replace(item.type.substring(0,1), item.type.substring(0,1).toUpperCase())
                }</Font>
                <Font margin='0 0 8px 4%'>Name: {item.name}</Font>
                {
                  (item.ppDocument.documentName || item.ubDocument.documentName) &&
                  isDocumentation &&
                  <Font
                    ellipsis={false}
                    bold
                    margin='0 0 8px 4%'
                  >
                    {t('onboarding.main.docPreviewText')}
                    {isLoading && t('onboarding.main.gettingPreview')}
                  </Font>
                }
                <InputFiles
                  key={`${item.ppDocument.documentID}-${globalConsts.INPUT_FILES_CORPORATE_DECLARATIONS_PPDOCUMENT}-${item.name}`}
                  name={globalConsts.INPUT_FILES_CORPORATE_DECLARATIONS_PPDOCUMENT}
                  label={t('onboarding.forms.CorporateAccount.DeclarationsForm.files.passportAndCurrentBill.inputLabel')}
                  serverFileName={{name: item.ppDocument.documentName, documentID: item.ppDocument.documentID.toString()}}
                  singleFileInput
                  // @ts-ignore:next-line
                  deleteFile={deleteFile}
                  onChange={(file, cb) => {
                    onChange(file, cb, item, globalConsts.INPUT_FILES_CORPORATE_DECLARATIONS_PPDOCUMENT)
                  }}
                  fileInputId={index}
                  margin='4px 0 0 0'
                  wrapperPadding='0 0 0 4%'
                  wrapperBoxSizing='border-box'
                  downloadPreviewClick={
                    (docId, name) => downloadPreview(sendRequest, flowKeyMap[`${flowKey}`], applicationId, docId, name)
                  }
                  isEmployee={isEmployee}
                  isDocumentation={isDocumentation}
                />
                <InputFiles
                  key={`${item.ubDocument.documentID}-${globalConsts.INPUT_FILES_CORPORATE_DECLARATIONS_UBDOCUMENT}-${item.name}`}
                  name={globalConsts.INPUT_FILES_CORPORATE_DECLARATIONS_UBDOCUMENT}
                  label={t('onboarding.forms.CorporateAccount.DeclarationsForm.files.currentBill.inputLabel')}
                  serverFileName={{name: item.ubDocument.documentName, documentID: item.ubDocument.documentID.toString()}}
                  singleFileInput
                  // @ts-ignore:next-line
                  deleteFile={deleteFile}
                  onChange={(file, cb) => {
                    onChange(file, cb, item, globalConsts.INPUT_FILES_CORPORATE_DECLARATIONS_UBDOCUMENT)
                  }}
                  fileInputId={index}
                  margin='0'
                  wrapperPadding='0 0 24px 4%'
                  wrapperBoxSizing='border-box'
                  downloadPreviewClick={
                    (docId, name) => downloadPreview(sendRequest, flowKeyMap[`${flowKey}`], applicationId, docId, name)
                  }
                  isEmployee={isEmployee}
                  isDocumentation={isDocumentation}
                />
              </InputFilesPerTypeFilesWrapper>
            )
          })
          : <Flex maxWidth justifyContent='center'>
            <Font tag='h3' color='error'>No directors or UBOs have been added, please refer to section 4 and 5.</Font>
          </Flex>
      }
    </Flex>
  )
}

export default InputFilesPerType
